import {Button, IconButton, Link} from '@primer/react'
import type {AllowedArrayOption, RegisteredRuleSchemaComponent} from '../../types/rules-types'
import {Blankslate} from '../Blankslate'
import {AlertIcon, PlusIcon, TrashIcon, TriangleDownIcon} from '@primer/octicons-react'
import React, {useMemo, useState} from 'react'
import {ItemPicker} from '@github-ui/item-picker-base/ItemPicker'

export function EnumSelector({field, value, onValueChange, readOnly}: RegisteredRuleSchemaComponent) {
  if (field.type !== 'array' || field.content_type === 'object' || field.allowed_options === undefined) {
    throw new Error('Field type must be array')
  }

  if (!Array.isArray(value)) {
    throw new Error('Value must be an array')
  }

  const selected = useMemo(
    () => value.map(v => field.allowed_options?.find(option => option.value === v)).filter(s => s !== undefined),
    [field, value],
  )

  const onValueUpdate = (newSelected: AllowedArrayOption[]) => {
    const newValues = newSelected.map(option => option.value)
    onValueChange?.(newValues)
  }

  const allSelected = selected.length === field.allowed_options.length

  const handleToggleAll = () => {
    if (allSelected) {
      onValueUpdate([])
    } else {
      onValueUpdate(field.allowed_options ?? [])
    }
  }

  return (
    <>
      {!readOnly ? (
        <div className="Box">
          <div className="Box-header d-flex flex-justify-between flex-items-center">
            <div className="Box-title">{field.display_name}</div>
            <div className="d-flex flex-items-center gap-2">
              <Button variant="invisible" size="small" type="button" onClick={handleToggleAll}>
                {allSelected ? 'Deselect all' : `Select all (${field.allowed_options.length})`}
              </Button>
              <EnumPicker value={selected} availableValues={field.allowed_options} onValueChange={onValueUpdate} />
            </div>
          </div>
          <EnumRows selectedEnums={selected} onValueChange={onValueUpdate} readOnly={readOnly} />
        </div>
      ) : (
        <EnumRows selectedEnums={selected} onValueChange={onValueUpdate} readOnly={readOnly} />
      )}
    </>
  )
}

function EnumPicker({
  value,
  availableValues,
  onValueChange,
}: {
  value: AllowedArrayOption[]
  availableValues: AllowedArrayOption[]
  onValueChange: (value: AllowedArrayOption[]) => void
}) {
  const [filterText, setFilterText] = useState('')

  const filteredValues = useMemo(() => {
    if (filterText.trim() === '') {
      return availableValues
    }
    return availableValues.filter(option => option.display_name.toLowerCase().includes(filterText.trim().toLowerCase()))
  }, [availableValues, filterText])

  const renderAnchor = (anchorProps: React.HTMLAttributes<HTMLElement>) => {
    return (
      <Button
        trailingAction={TriangleDownIcon}
        leadingVisual={PlusIcon}
        aria-label="Click to add values"
        {...anchorProps}
      >
        Add values
      </Button>
    )
  }

  const convertToItemProps = (item: AllowedArrayOption) => {
    return {
      id: item.value.toString(),
      text: item.display_name,
      source: item,
      ...(item.warning ? {trailingVisual: <AlertIcon aria-label={item.warning} />} : {}),
    }
  }

  return (
    <ItemPicker
      title="Add values"
      items={filteredValues}
      initialSelectedItems={value}
      getItemKey={item => item.value.toString()}
      convertToItemProps={convertToItemProps}
      selectionVariant={'multiple'}
      placeholderText="Select values"
      renderAnchor={renderAnchor}
      onSelectionChange={onValueChange}
      filterItems={setFilterText}
    />
  )
}

function EnumRows({
  selectedEnums,
  onValueChange,
  readOnly,
}: {
  selectedEnums: AllowedArrayOption[]
  onValueChange?: (value: AllowedArrayOption[]) => void
  readOnly?: boolean
}) {
  const sortedEnums = useMemo(() => {
    const pinned = selectedEnums.filter(e => e.pinned)
    const unpinned = selectedEnums.filter(e => !e.pinned)
    return [...pinned, ...unpinned]
  }, [selectedEnums])

  return (
    <>
      {sortedEnums.length ? (
        <ul>
          {sortedEnums.map(enumItem => (
            <li key={enumItem.value} className="Box-row d-flex flex-justify-between flex-items-center">
              <span className="d-flex flex-items-center gap-2 wb-break-word">
                {enumItem.display_name}
                {enumItem.warning && (
                  <IconButton icon={AlertIcon} variant="invisible" size="small" aria-label={enumItem.warning} />
                )}
                {enumItem.docs_url && (
                  <Link
                    href={enumItem.docs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    inline
                    aria-label={`Learn more about ${enumItem.display_name}`}
                  >
                    Learn more
                  </Link>
                )}
              </span>
              {!readOnly && (
                <IconButton
                  className="ml-2"
                  icon={TrashIcon}
                  type="button"
                  aria-label={`Delete ${enumItem.display_name}`}
                  size="small"
                  variant="invisible"
                  onClick={() => {
                    onValueChange?.(selectedEnums.filter(toDelete => toDelete.value !== enumItem.value))
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      ) : (
        <Blankslate heading="No values have been added" />
      )}
    </>
  )
}
